import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue, IconCode } from "@multiweb/types/contracts";
import { setupBem } from "Global/Dom/bem";
import { EditableControl as EditableControlBase } from "UI/UI.BaseControls/EditableControl";
import { EventHelper } from "UI/UI.BaseControls/Helpers/EventHelper";
import { IHTMLElementWithControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { EditableControl } from "UI/UI/UI/Controls/EditableControl";
import { PopupMenu2, PopupMenuOptions } from "UI/UI/UI/PopupMenu2";
import { setExtendedPropertiesOnElement } from "UI/utils";
import { IEncAttrValue } from "../../../../Global/Base/DataTypes/IAttrValue";
import { Event as MWEvent } from "../../../../Global/Base/Events";
import { invariant } from "../../../../Global/Base/Utils/AssertUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IBussyAwaitable, IDataLoadingAwaitable, IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { IEditItemTemplateMasterRef, ITemplate } from "../Templates/ITemplate";
import { Cell } from "./Cell";
import { ICellContent } from "./ICellContent";
import { IDataRow, IDataRowElement } from "./IDataRow";

const logger = LazyLoadingSectionLogger.create(LoggingSection.dataGrid);

export class TableCell extends Cell implements IValidatable, IBussyAwaitable, IDataLoadingAwaitable {
    private _validityChanged = new MWEvent<IValidatable, IValidationResult>();
    public currentValidity: IValidationResult;
    public isInRequiredInvalidState: boolean;

    protected _normalContent: ICellContent;
    protected _editableContent: ICellContent;
    protected _inEditMode: boolean = false;
    protected _dataSet: IAttrValue[];
    protected _dataRow: IDataRow;
    protected _validityData: IValidationResult;
    protected _textAreaElement: HTMLTextAreaElement;
    protected _valuePopup: PopupMenu2;

    private __isValid: boolean = true;
    private _pendingTasks: Array<Promise<void>> = [];

    /** Hodnota editovatelné buňky, se kterou byla inicializována. */
    private _initialValue: string;

    public get validityChanged(): MWEvent<IValidatable, IValidationResult> {
        invariant(this._validityChanged, "Cannot get event for disposed TableCell.");
        return this._validityChanged;
    }

    protected set _isValid(v: boolean) {
        if (this.__isValid === v) {
            return;
        }

        this.__isValid = v;
        this.validityChanged.trigger(this, v ? { valid: true } : this._validityData);
    }

    public get isValid(): boolean {
        return this.__isValid;
    }

    /**
     * Právě zobrazený element
     */
    public get element(): HTMLElement {
        return this._content?.visualElement;
    }

    /**
     * Právě použitý template
     */
    public get templateControl(): ITemplate {
        return this._inEditMode ? this.column.editItemTemplate : this.column.itemTemplate;
    }

    /**
     * Zobrazovaná data
     */
    public get dataSet(): IAttrValue[] {
        return this._dataSet;
    }

    public set dataSet(data: IAttrValue[]) {
        this._dataSet = data;

        this._updateData();

        this.validate();
    }

    /**
     * Právě používané elementy v závislosti na režimu editace
     */
    protected get _content(): ICellContent {
        return this._inEditMode ? this._editableContent : this._normalContent;
    }

    /**
     * Element, kterému se mají nastavovat a aktualizovat data
     */
    protected get _dataElement(): HTMLElement {
        return this._content?.dataElement;
    }

    public get isFocused(): boolean {
        if (this._editableContent?.visualElement instanceof IHTMLElementWithControl) {
            if (this._editableContent.visualElement.tscControl instanceof EditableControl) {
                return this._editableContent.visualElement.tscControl.hasFocus();
            }
        } else if (this._editableContent?.dataElement instanceof EditableControlBase) {
            return this._editableContent.dataElement.isFocused;
        } else if (this._editableContent?.dataElement instanceof IHTMLElementWithControl) {
            if (this._editableContent.dataElement.tscControl instanceof EditableControl) {
                return this._editableContent.dataElement.tscControl.hasFocus();
            }
        }
        return false;
    }

    public focus(): void {
        const listDiv = this.element.closest(".list-div");
        const scrollTop = listDiv?.scrollTop; // Safari nepodporuje preventScroll
        if (this._content?.dataElement instanceof IHTMLElementWithControl && this._content?.dataElement?.tscControl instanceof EditableControl) {
            this._content?.dataElement?.tscControl.focus({ preventScroll: true });
        } else {
            this._content?.dataElement?.focus({ preventScroll: true });
        }
        if (listDiv) {
            listDiv.scrollTop = scrollTop;
        }
    }

    public blur(): void {
        if (this._editableContent?.dataElement instanceof EditableControlBase) {
            this._editableContent.dataElement.blur();
        }
    }

    constructor(owner: IDataGrid<IDataRowElement>, row: IDataRow, index: number, column: Column, data: IAttrValue[]) {
        super(owner, index, column);
        this._dataSet = data;
        this._dataRow = row;
        this._owner.addEventListener("detached", this._disposeValuePopup);
    }

    private _disposeValuePopup(): void {
        if (this._valuePopup) {
            this._valuePopup.dispose();
            this._valuePopup = null;
        }
    }

    private _updateInitialValue(): void {
        if (this._editableContent?.dataElement instanceof EditableControlBase) {
            this._initialValue = this._editableContent.dataElement.value;
        } else if (
            this._editableContent?.dataElement instanceof IHTMLElementWithControl
            && this._editableContent.dataElement.tscControl instanceof EditableControl
        ) {
            this._initialValue = this._editableContent.dataElement.tscControl.controlValue;
        }
    }

    public toEdit(editMode: boolean): void {
        if (!this.column.isEditable) {
            return;
        }

        this._inEditMode = editMode;

        this._preRender();

        // pri ukonceni editace zesynchronizuje pripadnou nevalidni hodnotu do needitacni komponenty
        // u editacnich komponent neni potřeba znovu nastavovat nevalidni hodnotu, ta tam uu je
        if (!editMode) {
            this._syncInvalidData();
        }

        this._disposeHiddenElement();
    }

    protected _showValuePopup(e: Event): void {
        if (!this._valuePopup) {
            const wrapper = createElement({ classes: "MultilineWrapper" });
            wrapper.appendChild(this._textAreaElement);
            this._valuePopup = new PopupMenu2({
                target: this.element,
                content: wrapper,
                showPosition: "overlay",
                width: "content",
                closeButton: true,
            } as PopupMenuOptions);
        }

        this._textAreaElement.textContent = this._normalContent.dataElement.textContent;
        this._textAreaElement.style.height = "";
        this._textAreaElement.style.width = "";
        this._textAreaElement.style.display = "";
        this._valuePopup.show(e);
    }

    protected _hideValuePopup(): void {
        if (this._valuePopup) {
            this._valuePopup.close();
        }
    }

    protected _preRender(): void {
        try {
            this._hideValuePopup();
            // vytvoří elementy pokud jeste vytvořeny nebyly
            if (this.column.isEditable && this._inEditMode) {
                if (this._editableContent == null) {
                    this._editableContent = this.column.editItemTemplate.render(this.column, this._dataSet);
                    // Datový atribut dále používáme v procesu vyhodnocení práv editačních komponent na editovatelném
                    // ListFramu. Pokud id kontextu, se kterým se práva vyhodnocují, neodpovídá hodnotě tohoto atributu,
                    // tak vyhodnocení práv pro tuto komponentu přeskočíme.
                    this._editableContent.dataElement.setAttribute("data-row-datakey", this._dataRow.dataKey);
                    if (this.column.multiLine) {
                        this._editableContent.visualElement.appendChild(this._createMultilineButton(true));
                    }

                    if (this._editableContent.validatableControl) { // pouze editacni komponenty mohou menit data a tim i validitu
                        this._editableContent.validatableControl.validityChanged.add((_el, validity) => this._updateValidity(validity));
                    }

                    this._updateInitialValue();
                }
            } else {
                if (this._normalContent == null) {
                    this._normalContent = this.column.itemTemplate.render(this.column, this._dataSet);
                    this._updateEncryptedState();

                    if (this.column.multiLine) {
                        this._prepareMultiline();
                    }
                }
            }
            if (this.column.width) { // je nastavená a nenulová
                this.element.style.width = this.column.width + "px";
            }

            if (this._dataRow.getExtendedProperties) {
                const extendedProperties = this._dataRow.getExtendedProperties(this.column.attrName);
                if (extendedProperties) {
                    setExtendedPropertiesOnElement(extendedProperties, this.element);
                }
            }
        } catch (e) {
            logger.warn(e);
            logger.log(this._dataSet);
        }
    }

    protected _prepareMultiline(): void {
        this._textAreaElement = createElement({ type: "textarea", classes: ["Disabled"], attrs: { "readonly": "true" } }) as HTMLTextAreaElement;
        this._textAreaElement.classList.add("Multiline");
        this._textAreaElement.style.display = "none";
        this._owner.appendChild(this._textAreaElement);

        this._normalContent.visualElement.appendChild(this._createMultilineButton(false));

        this._normalContent.dataElement.addEventListener("updated", () => this._textAreaElement.textContent = this._normalContent.dataElement.textContent);
    }

    private _createMultilineButton(editableCell: boolean): HTMLButtonElement {
        const bemClass = setupBem("btn");
        const btn = createElement({ type: "button", classes: [bemClass.modifier("multiline"), "btn", "flat", "icon", IconCode.uiResizeBottomRight] }) as HTMLButtonElement;

        btn.addEventListener("click", (e) => {
            if (btn.classList.contains("Disabled")) {
                return;
            }
            EventHelper.stopPropagation(e);
            if (!editableCell) {
                this._toggleValuePopup(e);
            } else {
                (this._editableContent.visualElement.firstElementChild as HTMLElement).focus();
            }
        });

        if (!this._dataRow.isSelected) {
            btn.style.display = "none";
        }

        this._dataRow.selectionStateChanged.add((_, value: boolean) => {
            if (value) {
                btn.style.display = "";
            } else {
                btn.style.display = "none";
                this._hideValuePopup();
            }
        });

        return btn;
    }

    protected _toggleValuePopup(e: Event): void {
        if (this._valuePopup && this._valuePopup.visible) {
            this._hideValuePopup();
        } else {
            this._showValuePopup(e);
        }
    }

    public override preRender(): void {
        this._preRender();
    }

    public override render(): HTMLElement {
        if (this.column.visible) {
            this.preRender();
            return this.element;
        }

        return null;
    }

    /**
     * Přidá proces běžící na komponentě, na který je potřeba čekat
     * @param promises Procesy, na které je potřeba čekat
     */
    public addPendingActions(...promises: Promise<void>[]): void {
        this._pendingTasks.push(...promises);
    }

    /**
     * Umožňuje čekat na běžící procesy na komponentě
     */
    public async waitForPendingActions(): Promise<void> {
        await Promise.all(this._pendingTasks);
        this._pendingTasks = new Array<Promise<void>>();

        // ReSharper disable once SuspiciousInstanceofCheck
        if (this._dataElement instanceof IBussyAwaitable) {
            return this._dataElement.waitForPendingActions();
        }
        return Promise.resolve();
    }

    /**
     * Umožňuje čekat na dočtení dat
     */
    public async waitOnDataLoaded(): Promise<boolean> {
        // ReSharper disable once SuspiciousInstanceofCheck
        if (this._dataElement instanceof IDataLoadingAwaitable) {
            return this._dataElement.waitOnDataLoaded();
        }

        return Promise.resolve(true);
    }

    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    public cancelPendingActions(): void {
        // ReSharper disable once SuspiciousInstanceofCheck
        if (this._dataElement instanceof IBussyAwaitable) {
            this._dataElement.cancelPendingActions();
        }
    }

    /**
     * Vrací true, pokud došlo ke změně dat od inicializace buňky.
     */
    public isChanged(): boolean {
        if (this._editableContent?.dataElement instanceof EditableControlBase) {
            // We compare after converting to string, because the value can be a Date instance
            // that is different in _initialValue and _value, but represents the same date.
            return !StringUtils.stringCompare(this._initialValue, this._editableContent.dataElement.value);
        } else if (
            this._editableContent?.dataElement instanceof IHTMLElementWithControl
            && this._editableContent.dataElement.tscControl instanceof EditableControl
        ) {
            // We compare after converting to string, because the value can be a Date instance
            // that is different in _initialValue and _value, but represents the same date.
            return !StringUtils.stringCompare(this._initialValue, this._editableContent.dataElement.tscControl.controlValue);
        }

        return false;
    }

    protected _updateData(): void {
        try {
            // když prijdou nova data, tak je potreba nastavit obou komponent (pokud uz byly vytvoreny)
            if (this._normalContent && this._normalContent.dataElement) {
                this.column.itemTemplate.updateData(this._dataSet, this._normalContent.dataElement);
                this._updateEncryptedState();
            }

            if (this._editableContent && this._editableContent.dataElement) {
                this.column.editItemTemplate.updateData(this._dataSet, this._editableContent.dataElement);
                this._updateInitialValue();
            }
        } catch (e) {
            logger.warn(e);
            logger.log(this._dataSet);
        }
    }

    protected _syncInvalidData(): void {
        try {
            if (!this.isValid) {
                this.templateControl.updateDisplayedData(this._validityData.invalidValue, this._dataElement);
            } /*else {
                    this.TemplateControl.UpdateData(this._data, this._dataElement);
                }*/
        } catch (e) {
            logger.warn(e);
            logger.log(this._dataSet, this._validityData.invalidValue);
        }
    }

    public validate(): boolean {
        let valid = true;

        // validace se provede na obou komponentách (pokud existují), aby si obě správně aktualizovaly svou validitu

        if (this._editableContent && this._editableContent.validatableControl) {
            valid = this._editableContent.validatableControl.validate();
        }

        if (this._normalContent && this._normalContent.validatableControl) {
            valid = this._normalContent.validatableControl.validate() && valid;
        }

        this._isValid = valid;
        return this.isValid;
    }

    public setValid(): void {
        if (this._editableContent && this._editableContent.validatableControl) {
            this._editableContent.validatableControl.setValid();
        }

        if (this._normalContent && this._normalContent.validatableControl) {
            this._normalContent.validatableControl.setValid();
        }

        this.__isValid = true;
    }

    public setInvalid(locKey: string, message?: string): void {
        if (this._editableContent && this._editableContent.validatableControl) {
            this._editableContent.validatableControl.setInvalid(locKey, message);
        }

        if (this._normalContent && this._normalContent.validatableControl) {
            this._normalContent.validatableControl.setInvalid(locKey, message);
        }

        this.__isValid = false;
    }

    private _disposeHiddenElement(): void {
        if (this._inEditMode && this._normalContent != null) {
            this._normalContent.detached();
        } else if (!this._inEditMode && this._editableContent != null) {
            this._editableContent.detached();
        }
    }

    protected _updateValidity(validity: IValidationResult): void {
        this._validityData = validity;
        this._isValid = validity.valid;

        // vznikla zmena validity se prenese i na needitovatelnou komponentu, ktera to podporuje
        if (this._normalContent && this._normalContent.validatableControl) {
            validity.valid ? this._normalContent.validatableControl.setValid() : this._normalContent.validatableControl.setInvalid(validity.invalidLocKey, validity.invalidMessage);
        }
    }

    /**
     * Změní stav buňky podle toho, zda jsou zobrazovaná data šifrovaná
     */
    protected _updateEncryptedState(): void {
        const dataEncrypted = this._isDataEncrypted(this._dataSet);
        if (this._normalContent.visualElement.classList.contains("encrypted") === dataEncrypted) {
            return;
        }

        this._normalContent.visualElement.classList.toggle("encrypted", dataEncrypted);
        if (this._normalContent.encryptionElement == null) {
            this._normalContent.encryptionElement = createElement({ classes: ["encrypted-symbol", "icon", "user-lock"] });
            this._normalContent.visualElement.insertAdjacentElement("afterbegin", this._normalContent.encryptionElement);
        }
    }

    /**
     * Zjistí, zda jsou zobrazovaná data šifrovaná
     * @param dataSet Zobrazovaná data
     */
    protected _isDataEncrypted(dataSet: IAttrValue[]): boolean {
        if (!dataSet) {
            return false;
        }

        for (const data of dataSet) {
            if (data instanceof IEncAttrValue && data.isEncrypted) {
                return true;
            }
        }

        return false;
    }

    public setInstance(obj: OWObject): void {
        if (!this.dataSet || !(this.dataSet instanceof Array) || this.dataSet.length === 0) {
            return;
        }

        if (this.column.editItemTemplate && this.column.editItemTemplate instanceof IEditItemTemplateMasterRef) {
            if (this._editableContent && this._editableContent.dataElement) {
                this.column.editItemTemplate.setInstance(obj, this._editableContent.dataElement);
            }
            if (obj && this.element && this.element.firstChild) {
                const param = {
                    detail: { control: this.element.firstChild, objectId: obj.id, data: obj },
                    bubbles: true,
                    cancelable: true,
                };
                const evt = new CustomEvent("masterObjectRequest", param);
                this.column.dispatchEvent(evt);
            }
        }
    }

    public override dispose(): void {
        this._owner.removeEventListener("detached", this._disposeValuePopup);
        this._disposeValuePopup();
        this._validityChanged.removeAll();

        super.dispose();
    }
}
